import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings, SlidersHorizontal } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { ShellPopover } from './ShellPopover';
import { useReduceMotion } from './useReduceMotion';

/**
 * App preferences.
 *
 * CT-0 also lists "Sounds" and "Open on launch". The app plays no sounds, so
 * a switch for them would control nothing; "Open on launch" is left for when
 * the frame that defines it can be read, since it is a choice of page rather
 * than an on/off.
 */
export const PreferencesPopover: React.FC = () => {
  const { t } = useTranslation();
  const [reduceMotion, toggleReduceMotion] = useReduceMotion();
  const label = t('app.v11.shell.preferences.title');

  return (
    <ShellPopover
      ariaLabel={label}
      trigger={
        <button
          type="button"
          aria-label={label}
          title={label}
          className="flex h-9 w-9 items-center justify-center rounded-[8px] text-[rgba(11,11,11,0.7)] transition hover:bg-v11-surface-hover dark:text-white/70"
        >
          <Settings className="h-[18px] w-[18px]" strokeWidth={1.6} />
        </button>
      }
    >
      <div className="p-2">
        <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm text-v11-text-secondary transition hover:bg-v11-surface-hover">
          <span className="flex items-center gap-3">
            <SlidersHorizontal className="h-4 w-4" />
            {t('app.v11.shell.preferences.reduceMotion')}
          </span>
          <Switch
            checked={reduceMotion}
            onCheckedChange={toggleReduceMotion}
            aria-label={t('app.v11.shell.preferences.reduceMotion')}
          />
        </label>

        <div className="my-1 h-px bg-v11-border" />

        <Link
          to="/v11/settings"
          className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-v11-text-secondary transition hover:bg-v11-surface-hover hover:text-v11-text-primary"
        >
          <Settings className="h-4 w-4" />
          {t('app.v11.shell.preferences.openAccountSettings')}
        </Link>
      </div>
    </ShellPopover>
  );
};
